// Map a plant/variety/species label to a marker colour that reads at a glance:
// the base hue aligns to the fruit's real colour (mango → amber, lemon → yellow,
// avocado → dark green, …), and a small deterministic per-name variance shifts the
// shade so two varieties of the same fruit differ (Keitt vs Haden mango) while both
// still read "mango". Citrus and mango are deliberately distinct base hues.
//
// Used by the farm map tree markers (and reusable anywhere a per-variety swatch
// helps). Keyword order matters — more specific terms first.

const FRUIT_COLORS: [RegExp, string][] = [
  [/lemon|meyer/, '#FCD34D'],
  [/\blime\b|key ?lime/, '#A3E635'],
  [/grapefruit|pomelo/, '#FB7185'],
  [/tangerine|mandarin|clementine|satsuma|kumquat/, '#FB923C'],
  [/orange|navel|citrus|calamondin/, '#F97316'],
  [/mango/, '#F59E0B'],
  [/avocado/, '#4D7C0F'],
  [/banana|plantain/, '#FDE047'],
  [/lychee|rambutan|longan|mamoncillo/, '#E11D48'],
  [/jackfruit|breadfruit|\bjak\b|cempedak/, '#65A30D'],
  [/papaya/, '#FB923C'],
  [/guava/, '#FBBF24'],
  [/passion ?fruit|lilikoi/, '#7C3AED'],
  [/dragon ?fruit|pitaya/, '#EC4899'],
  [/\bfig\b/, '#7E22CE'],
  [/pineapple/, '#EAB308'],
  [/coconut|\bpalm\b|date/, '#A16207'],
  [/star ?fruit|carambola/, '#CA8A04'],
  [/sapodilla|sapote|mamey/, '#92400E'],
  [/peach|nectarine|apricot/, '#FB7185'],
  [/\bplum\b|pluot/, '#9333EA'],
  [/\bapple\b/, '#EF4444'],
  [/\bpear\b/, '#A3E635'],
  [/cherry/, '#DC2626'],
  [/\bgrape\b|muscadine/, '#7C3AED'],
  [/strawberr|blueberr|blackberr|raspberr|mulberr|\bberry\b/, '#E11D48'],
  [/pomegranate/, '#BE123C'],
  [/persimmon/, '#EA580C'],
  [/loquat/, '#F59E0B'],
  [/olive/, '#6B7C3A'],
  [/cacao|cocoa/, '#78350F'],
  [/coffee/, '#7C2D12'],
  [/almond|pecan|walnut|macadamia|cashew|chestnut|\bnut\b/, '#A16207'],
  [/soursop|cherimoya|atemoya|sugar ?apple|custard ?apple|annona/, '#86EFAC'],
  [/tamarind/, '#B45309'],
]
const DEFAULT_FRUIT = '#22C55E' // leafy / unknown

function hashName(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0
  return h
}
// Deterministic variance in [-1, 1) from a name.
function variance(s: string): number {
  if (!s) return 0
  return (hashName(s) % 200) / 100 - 1
}

function hexToHsl(hex: string): [number, number, number] {
  const m = hex.replace('#', '')
  const r = parseInt(m.slice(0, 2), 16) / 255
  const g = parseInt(m.slice(2, 4), 16) / 255
  const b = parseInt(m.slice(4, 6), 16) / 255
  const max = Math.max(r, g, b), min = Math.min(r, g, b)
  let h = 0
  const l = (max + min) / 2
  const d = max - min
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1))
  if (d !== 0) {
    if (max === r) h = ((g - b) / d) % 6
    else if (max === g) h = (b - r) / d + 2
    else h = (r - g) / d + 4
    h *= 60
    if (h < 0) h += 360
  }
  return [h, s, l]
}
function hslToHex(h: number, s: number, l: number): string {
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const mm = l - c / 2
  let r = 0, g = 0, b = 0
  if (h < 60) [r, g, b] = [c, x, 0]
  else if (h < 120) [r, g, b] = [x, c, 0]
  else if (h < 180) [r, g, b] = [0, c, x]
  else if (h < 240) [r, g, b] = [0, x, c]
  else if (h < 300) [r, g, b] = [x, 0, c]
  else [r, g, b] = [c, 0, x]
  const to = (v: number) => Math.round((v + mm) * 255).toString(16).padStart(2, '0')
  return `#${to(r)}${to(g)}${to(b)}`
}

/** Fruit-aligned marker colour for a plant/variety/species label, with a small
 *  per-name shade variance so varieties of the same fruit differ. */
export function varietyColor(label?: string | null): string {
  const s = (label || '').toLowerCase()
  let base = DEFAULT_FRUIT
  for (const [re, hex] of FRUIT_COLORS) {
    if (re.test(s)) { base = hex; break }
  }
  const [h, sat, l] = hexToHsl(base)
  // Nudge lightness ±8% (keep hue/sat so the fruit identity holds).
  const nl = Math.max(0.25, Math.min(0.82, l + variance(s) * 0.08))
  return hslToHex(h, sat, nl)
}
